import {
	IconButton,
	Portal
} from "client/Components";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { MenuItem } from "./MenuItem/MenuItem";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./MainMenu.module.less";
import { MobileMenuItem, useMenuItems } from "./MenuItem/useMenuItems";
import { useHandleNavigationInGlobalSubNavigation } from "../../GlobalNavigationSidebar/useGlobalNavigationVisibilityState";
import { useFrequentlyUsedTiles } from "../../FrequentlyUsedTiles/useFrequentlyUsedTiles";
import { FrequentlyUsedTilesList } from "../../FrequentlyUsedTiles/FrequentlyUsedTilesList";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { useFrequentlyUsedTileNavigation } from "../../FrequentlyUsedTiles/useFrequentlyUsedTileNavigation";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";

export const MainMenu = (): ReactElement => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const items: readonly MobileMenuItem[] = useMenuItems();
	const userTimeZone: TimeZone = useUserTimezone();
	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();;

	const itemsToRender = useMemo(
		(): {
			visibleItems: readonly MobileMenuItem[];
			hiddenItems: readonly MobileMenuItem[];
		} => {
			if (items.length <= MAX_ITEM_COUNT) {
				return {
					visibleItems: items,
					hiddenItems: []
				};
			}

			const firstInvisibleItemIndex: number = MAX_ITEM_COUNT;
			const reorderedItems: MobileMenuItem[] = [...items];
			const hiddenItems: readonly MobileMenuItem[] = items.slice(firstInvisibleItemIndex);

			const activeHiddenItemIndex: number = hiddenItems.findIndex(
				x => x.isActive
			);

			/** Swap the active hidden item
			 *  with the last visible item
			 * */
			if (activeHiddenItemIndex !== -1) {
				[
					reorderedItems[firstInvisibleItemIndex - 1],
					reorderedItems[firstInvisibleItemIndex + activeHiddenItemIndex]
				] = [
					reorderedItems[firstInvisibleItemIndex + activeHiddenItemIndex],
					reorderedItems[firstInvisibleItemIndex - 1]
				];
			}

			return {
				visibleItems: reorderedItems.slice(0, firstInvisibleItemIndex),
				hiddenItems: reorderedItems.slice(firstInvisibleItemIndex)
			};
		},
		[items]
	);

	const itemClickHandler = useCallback(
		(item: MobileMenuItem): void => {
			handleNavigationInGlobalSubNavigation();

			setIsOpen(false);

			item.onClick();
		},
		[handleNavigationInGlobalSubNavigation]
	);

	const openMoreItemHandler = useCallback(
		(): void => {
			handleNavigationInGlobalSubNavigation();

			setIsOpen(!isOpen);
		},
		[handleNavigationInGlobalSubNavigation, isOpen]
	);

	return (
		<div
			{...setTrackingId("mobile-main-menu")}
			className={styles.root}
		>
			<Portal container={document.body}>
				<div
					className={classNames(
						styles.backdrop,
						{
							[styles.backdrop_isVisible]: isOpen
						}
					)}
					onClick={() => setIsOpen(false)}
				/>
			</Portal>
			<div className={styles.bar}>
				<ul className={styles.list}>
					{itemsToRender.visibleItems.map(
						x => (
							<MenuItem
								key={x.uniqueKey}
								icon={x.icon}
								isActive={x.isActive}
								count={x.counter}
								onClick={() => itemClickHandler(x)}
								trackingId={x.trackingId}
							/>
						)
					)}
					<MenuItem
						icon={{
							kind: "icon-value",
							value: "ellipsis"
						}}
						onClick={openMoreItemHandler}
						trackingId="more-items-button"
					/>
				</ul>
			</div>
			<DropOver
				isOpen={isOpen}
				hiddenItems={itemsToRender.hiddenItems}
				userTimeZone={userTimeZone}
				onClose={() => setIsOpen(false)}
				onClickItem={itemClickHandler}
			/>
		</div>
	);
};
MainMenu.displayName="MainMenu";

const MAX_ITEM_COUNT: number = 5;

type DropOverProps = {
	isOpen: boolean;
	hiddenItems: readonly MobileMenuItem[];
	userTimeZone: TimeZone;
	onClose(): void;
	onClickItem(item: MobileMenuItem): void;
};

const DropOver = (props: DropOverProps): ReactElement => {
	const {
		isOpen,
		hiddenItems,
		userTimeZone,
		onClose,
		onClickItem
	} = props;

	const [frequentlyUsedTiles] = useFrequentlyUsedTiles();
	const [tileNavigationElement, originalInvokeTileNavigationHandler] = useFrequentlyUsedTileNavigation(userTimeZone);

	const invokeTileNavigationHandler = useCallback(
		(item: FrequentlyUsedTileModel): void => {
			onClose();
			originalInvokeTileNavigationHandler(item);
		},
		[onClose, originalInvokeTileNavigationHandler]
	);

	return (
		<>
			<div
				className={classNames(
					styles.dropOver,
					{
						[styles.dropOver_isOpen]: isOpen
					}
				)}
			>
				<div className={styles.dropOverInner}>
					<div className={styles.dropOverHeader}>
						<h2>
							{localize("_Apps")}
						</h2>
						<IconButton
							onClick={onClose}
							kind="secondary"
							icon="xmark"
							size="large"
							shape="round"
							trackingId="close-button"
						/>
					</div>

					{(isNil(frequentlyUsedTiles) || frequentlyUsedTiles.length > 0) && (
						<>
							<div className={styles.frequentlyUsedTiles}>
								{isNotNil(frequentlyUsedTiles) && (
									<h4 className={styles.subHeader}>
										{localize("_FrequentlyUsedApps")}
									</h4>
								)}
								<FrequentlyUsedTilesList
									items={frequentlyUsedTiles}
									onInvokeTileNavigation={invokeTileNavigationHandler}
									trackingId="frequently-used-tiles"
								/>
							</div>

							<hr className={styles.divider} />
						</>
					)}

					<ul
						className={styles.dropOverList}
						style={{
							"gridTemplateColumns": `repeat(${MAX_ITEM_COUNT + 1}, 1fr)`
						}}
					>
						{hiddenItems.map(
							x => (
								<MenuItem
									key={x.uniqueKey}
									icon={x.icon}
									isActive={x.isActive}
									count={x.counter}
									onClick={() => onClickItem(x)}
									trackingId={x.trackingId}
								/>
							)
						)}
					</ul>
				</div>
			</div>

			{tileNavigationElement}
		</>
	);
};
DropOver.displayName="DropOver";